import moment from 'moment';
import {combineLatest, EMPTY, forkJoin, Observable, of} from 'rxjs';
import {catchError, filter, map, switchMap} from 'rxjs/operators';
import {SecurityService} from '../../../components/security';
import {AspreaderSecurityLeg, parseAsecurityConfig} from '../../../components/security/aspreader-security';
import {ResolutionString} from '../../../components/trading-view-chart/charting-library/datafeed-api';
import {CandlePeriod} from '../../../enums/candle-period';
import {Side} from '../../../enums/side';
import {Candle} from '../../../models/candle';
import {CommonListFilter} from '../../../models/filters/common-list-filter';
import {FilterCriterion} from '../../../models/filters/filter-criterion';
import {QueryParamFilterCriterion} from '../../../models/filters/query-param-filter-criterion';
import {Security} from '../../../models/security';
import {formatSecurityKey, parseSecurityKey, SecurityKey} from '../../../models/security-key';
import {ArrayEncodedCandle, ArrayEncodedCandleFieldIndex, CandlesResponse} from './candles-rest-api-response';

export const CANDLE_LIST_FILTER_SECURITY_KEY_FIELD_NAME: string = 'securityKey';

export const getSecurityKeyCandleFilterCriterion = (securityKey: SecurityKey): QueryParamFilterCriterion =>
    new QueryParamFilterCriterion({
        query:          formatSecurityKey(securityKey),
        queryParamName: CANDLE_LIST_FILTER_SECURITY_KEY_FIELD_NAME
    });

const findSecurityKeyFilterIndexInCandleFilters = (filterCriteria: FilterCriterion[]): number =>
    filterCriteria.findIndex(itFilterCriterion =>
        itFilterCriterion instanceof QueryParamFilterCriterion
        && itFilterCriterion.queryParamName === CANDLE_LIST_FILTER_SECURITY_KEY_FIELD_NAME
    );

export const getSecurityKeyFromCandleFilters = (filterCriteria: FilterCriterion[]): SecurityKey | undefined => {
    const filterCriterionIndex = findSecurityKeyFilterIndexInCandleFilters(filterCriteria);

    return (filterCriterionIndex >= 0)
        ? parseSecurityKey(filterCriteria[filterCriterionIndex].query)
        : undefined;
};

const findPeriodFilterIndexInCandleFilters = (filterCriteria: FilterCriterion[]): number =>
    filterCriteria.findIndex(itFilterCriterion =>
        itFilterCriterion instanceof QueryParamFilterCriterion
        && itFilterCriterion.queryParamName === 'candlePeriod'
    );

export const getPeriodFromCandleFilters = (filterCriteria: FilterCriterion[]): CandlePeriod => {
    const filterCriterionIndex = findPeriodFilterIndexInCandleFilters(filterCriteria);

    return (filterCriterionIndex >= 0)
        ? filterCriteria[filterCriterionIndex].query
        : undefined;
};

const getCandleListFilterWithoutSecurityKey = (filterCriteria: FilterCriterion[]): FilterCriterion[] => {
    let resultFilters: FilterCriterion[] = filterCriteria;

    const filterCriterionIndex = findSecurityKeyFilterIndexInCandleFilters(filterCriteria);

    if (filterCriterionIndex >= 0) {
        resultFilters = [...filterCriteria];

        resultFilters.splice(filterCriterionIndex, 1);
    }

    return resultFilters;
};

const calcMergedCandleValue = (
    leg1: AspreaderSecurityLeg,
    leg2: AspreaderSecurityLeg,
    val1: number,
    val2: number
): number | null => {
    let result: number | null = null;

    if (val1 && val2) {
        result = val1 * leg1.priceRatio * (leg1.side === Side.BUY ? 1 : -1)
            + val2 * leg2.priceRatio * (leg2.side === Side.BUY ? 1 : -1);
    }

    return result;
};

const mergeLegsCandles = (
    leg1: AspreaderSecurityLeg,
    leg2: AspreaderSecurityLeg,
    leg1Candles: ArrayEncodedCandle[],
    leg2Candles: ArrayEncodedCandle[]
): ArrayEncodedCandle[] => {
    const leg1CandlesLen = leg1Candles.length;
    const leg2CandlesLen = leg2Candles.length;

    const resultCandles: ArrayEncodedCandle[] = [];

    let iLeg1Candle: number = 0;
    let iLeg2Candle: number = 0;

    const addResultCandle = (candle: ArrayEncodedCandle): void => {
        resultCandles.push(candle);
    };

    const calcVal = (val1: number, val2: number): number | null =>
        calcMergedCandleValue(leg1, leg2, val1, val2);

    const mergeCandles = (candle1: ArrayEncodedCandle, candle2: ArrayEncodedCandle): ArrayEncodedCandle => {
        const [c1Id, c1OpenPrice, c1HighPrice, c1LowPrice, c1ClosePrice, , c1CloseTime] = candle1;
        const [, c2OpenPrice, c2HighPrice, c2LowPrice, c2ClosePrice] = candle2;

        const openPrice = calcVal(c1OpenPrice, c2OpenPrice);
        const closePrice = calcVal(c1ClosePrice, c2ClosePrice);
        const possibleHighPrice = calcVal(c1HighPrice, c2HighPrice);
        const possibleLowPrice = calcVal(c1LowPrice, c2LowPrice);

        const highLowVariants: number[] = [
            openPrice,
            closePrice,
            possibleHighPrice,
            possibleLowPrice
        ].filter(v => v !== undefined && v !== null);

        return [
            c1Id,
            openPrice,
            highLowVariants.length ? Math.max(...highLowVariants) : null,
            highLowVariants.length ? Math.min(...highLowVariants) : null,
            closePrice,
            null,
            c1CloseTime
        ];
    };

    const mergeCandlesToResult = (candle1: ArrayEncodedCandle, candle2: ArrayEncodedCandle): void =>
        addResultCandle(mergeCandles(candle1, candle2));

    const parseCandleTimestamp = (timeStr: string): number => moment(timeStr).valueOf();

    let leg1Candle: ArrayEncodedCandle | undefined;
    let leg2Candle: ArrayEncodedCandle | undefined;

    while (iLeg1Candle < leg1CandlesLen || iLeg2Candle < leg2CandlesLen) {
        leg1Candle = (iLeg1Candle < leg1CandlesLen)
            ? leg1Candles[iLeg1Candle]
            : undefined;
        leg2Candle = (iLeg2Candle < leg2CandlesLen)
            ? leg2Candles[iLeg2Candle]
            : undefined;

        if (leg1Candle && leg2Candle) {
            const leg1CandleTimeStr = leg1Candle[ArrayEncodedCandleFieldIndex.CLOSE_TIME];
            const leg2CandleTimeStr = leg2Candle[ArrayEncodedCandleFieldIndex.CLOSE_TIME];

            if (leg1CandleTimeStr === leg2CandleTimeStr) {
                mergeCandlesToResult(leg1Candle, leg2Candle);
                iLeg1Candle++;
                iLeg2Candle++;
            } else if (parseCandleTimestamp(leg1CandleTimeStr) > parseCandleTimestamp(leg2CandleTimeStr)) {
                iLeg2Candle++;
            } else {
                iLeg1Candle++;
            }
        } else {
            if (leg1Candle) {
                iLeg1Candle++;
            } else {
                iLeg2Candle++;
            }
        }
    }

    return resultCandles;
};

export const createAsecurityCandlesRequest = (
    asecurityKey: SecurityKey,
    listFilter: CommonListFilter,
    commonCandlesRequestFactory: (listFilter: CommonListFilter) => Observable<CandlesResponse>,
    securityService: SecurityService
): Observable<CandlesResponse> =>
    securityService.getSecurityBySecurityKey(asecurityKey).pipe(
        switchMap(asecurity => {
            let result$: Observable<CandlesResponse>;

            if (asecurity) {
                const commonFilterCriteria = getCandleListFilterWithoutSecurityKey(listFilter.filters);
                const asecurityConfig = parseAsecurityConfig(asecurity);
                const legsRequests = asecurityConfig.legs.map(leg => {
                    const legListFilter: CommonListFilter = {
                        ...listFilter,
                        filters: [
                            ...commonFilterCriteria,
                            getSecurityKeyCandleFilterCriterion(leg.securityKey)
                        ]
                    };

                    return commonCandlesRequestFactory(legListFilter);
                });

                result$ = forkJoin(legsRequests).pipe(
                    map<CandlesResponse[], CandlesResponse>(legsResponses => {
                        const result: CandlesResponse = {
                            candlePeriod: legsResponses[0].candlePeriod,
                            candles:      mergeLegsCandles(
                                asecurityConfig.legs[0],
                                asecurityConfig.legs[1],
                                legsResponses[0].candles,
                                legsResponses[1].candles
                            ),
                            security:     asecurity
                        };

                        return result;
                    }),
                    catchError(() => of<CandlesResponse>({
                        candlePeriod: getPeriodFromCandleFilters(listFilter.filters),
                        candles:      [],
                        security:     asecurity
                    }))
                );
            } else {
                result$ = EMPTY;
            }

            return result$;
        })
    );

const mergeStreamingCandles = (
    security: Security,
    leg1: AspreaderSecurityLeg,
    leg2: AspreaderSecurityLeg,
    candle1: Candle,
    candle2: Candle
): Candle => {
    const openPrice = calcMergedCandleValue(leg1, leg2, candle1.openPrice, candle2.openPrice);
    const closePrice = calcMergedCandleValue(leg1, leg2, candle1.closePrice, candle2.closePrice);
    const possibleHighPrice = calcMergedCandleValue(leg1, leg2, candle1.highPrice, candle2.highPrice);
    const possibleLowPrice = calcMergedCandleValue(leg1, leg2, candle1.lowPrice, candle2.lowPrice);

    const highLowVariants: number[] = [
        openPrice,
        closePrice,
        possibleHighPrice,
        possibleLowPrice
    ].filter(v => v !== undefined && v !== null);

    return {
        id:           candle1.id,
        candlePeriod: candle1.candlePeriod,
        closePrice,
        closeTime:    candle1.closeTime,
        highPrice:    highLowVariants.length ? Math.max(...highLowVariants) : null,
        lowPrice:     highLowVariants.length ? Math.min(...highLowVariants) : null,
        openPrice,
        securityKey:  candle1.securityKey,
        volume:       null,
        security
    };
};

export const createAsecurityCandlesStream = (
    asecurityKey: SecurityKey,
    resolution: ResolutionString,
    commonCandlesStreamFactory: (asecurityKeyStr: string, resolution: ResolutionString) => Observable<Candle>,
    securityService: SecurityService
): Observable<Candle> =>
    securityService.getSecurityBySecurityKey(asecurityKey).pipe(
        switchMap(asecurity => {
            let result$: Observable<Candle>;

            if (asecurity) {
                const asecurityConfig = parseAsecurityConfig(asecurity);
                const legsStreams = asecurityConfig.legs.map(leg =>
                    commonCandlesStreamFactory(formatSecurityKey(leg.securityKey), resolution)
                );

                result$ = combineLatest(legsStreams).pipe(
                    map(([candle1, candle2]) => {
                        let resultCandle: Candle;

                        if (candle1 && candle2 && candle1.closeTime === candle2.closeTime) {
                            resultCandle = mergeStreamingCandles(
                                asecurity,
                                asecurityConfig.legs[0],
                                asecurityConfig.legs[1],
                                candle1,
                                candle2
                            );
                        }

                        return resultCandle;
                    }),
                    filter(candle => !!candle)
                );
            } else {
                result$ = EMPTY;
            }

            return result$;
        })
    );
